/**
 * FOS unified-identity cross-product handoff resolvers (tt-rm-3).
 *
 * Three distinct handoff cases, deliberately routed differently:
 *
 *  - SwitchToFos session-expired fallback → the FOS DASHBOARD directly
 *    (`resolveFosDashboardUrl`). The member is an already-authenticated member
 *    switching products; the dashboard handles auth itself, so there's no need
 *    to bounce them through the login front-door (no `/login`, no
 *    `return_origin`).
 *  - Governance post-logout redirect → the login FRONT-DOOR
 *    (`resolveFosFrontdoorLoginUrl`). The member is genuinely logged out, so a
 *    fresh re-authenticate at the front-door is correct.
 *  - Login forgot-password link → the front-door `/forgot-password`
 *    (`resolveFosFrontdoorBaseUrl`).
 *
 * The old fallbacks pointed at the dead `think-tank.helloworlddao.com` host —
 * Think Tank is deprecated (unified under FounderyOS) and that host no longer
 * resolves.
 *
 * Both bases are env-driven, resolved at BUILD time via `import.meta.env`. The
 * deploy workflow injects them as Dockerfile build-args sourced from
 * `${{ vars.VITE_… || '<staging-default>' }}`; the Dockerfile carries staging
 * defaults as ARGs so a bare `docker build` still produces a working image.
 *
 *   front-door  staging → https://register.staging.founderyos.dev
 *   front-door  prod    → https://register.founderyos.dev
 *   dashboard   staging → https://dashboard.staging.founderyos.dev
 *   dashboard   prod    → https://dashboard.founderyos.dev
 *
 * Resolve the URL at call-time from the env — never hardcode the base, and never
 * reintroduce a think-tank host.
 *
 * @module components/layout/fos-frontdoor-url
 */

/** Last-resort fallbacks if the env vars are absent (e.g. a bare local build). */
export const DEFAULT_FOS_REGISTER_FRONTDOOR_URL = 'https://register.staging.founderyos.dev';
export const DEFAULT_FOS_DASHBOARD_URL = 'https://dashboard.staging.founderyos.dev';

/**
 * Env bag shape consumed by the resolver. Passing the bag explicitly (rather
 * than reading `import.meta.env` directly) keeps the resolver unit-testable
 * across staging/prod without mutating global build env.
 */
type EnvBag = Record<string, string | undefined>;

const importMetaEnv = (): EnvBag => import.meta.env as unknown as EnvBag;

/** Trim whitespace + drop any trailing slash; fall back to default when empty. */
function normalizeBaseUrl(raw: string | undefined | null, fallback: string): string {
  const value = (raw ?? '').trim();
  if (value === '') return fallback;
  return value.replace(/\/+$/, '');
}

/** Resolve the unified UID front-door base from the env bag. */
export function resolveFosFrontdoorBaseUrl(env: EnvBag = importMetaEnv()): string {
  return normalizeBaseUrl(env.VITE_FOS_REGISTER_FRONTDOOR_URL, DEFAULT_FOS_REGISTER_FRONTDOOR_URL);
}

/** Resolve the FOS dashboard origin used as the `return_origin` handoff target. */
export function resolveFosDashboardReturnOrigin(env: EnvBag = importMetaEnv()): string {
  return normalizeBaseUrl(env.VITE_FOS_DASHBOARD_URL, DEFAULT_FOS_DASHBOARD_URL);
}

/**
 * Resolve the SwitchToFos session-expired fallback target: the FOS dashboard
 * origin directly. The member is already authenticated and switching products,
 * so the dashboard handles auth itself — no `/login` bounce, no `return_origin`.
 * (Coby directive: SwitchToFos goes straight to the dashboard, not the
 * front-door.)
 */
export function resolveFosDashboardUrl(env: EnvBag = importMetaEnv()): string {
  return normalizeBaseUrl(env.VITE_FOS_DASHBOARD_URL, DEFAULT_FOS_DASHBOARD_URL);
}

/**
 * Build the post-logout re-auth target (Governance layout): send the genuinely
 * logged-out member to re-authenticate at the unified UID front-door, returning
 * to the FOS dashboard on success.
 *
 *   `${frontdoor}/login?return_origin=${dashboard}`
 *
 * The front-door mints a `?code=` handoff back to the dashboard origin after
 * sign-in. tt-rm-3: replaces the dead `think-tank.helloworlddao.com/login` host.
 * NOTE: this is the logout case only — the SwitchToFos (already-authenticated)
 * cross-product handoff uses `resolveFosDashboardUrl` (dashboard directly).
 */
export function resolveFosFrontdoorLoginUrl(env: EnvBag = importMetaEnv()): string {
  const frontdoor = resolveFosFrontdoorBaseUrl(env);
  const returnOrigin = resolveFosDashboardReturnOrigin(env);
  return `${frontdoor}/login?return_origin=${encodeURIComponent(returnOrigin)}`;
}
